import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { toast } from '@/design-system/toaster';
import type { Pitstop } from '@/services/api/pitstops';
import {
  Direct4WEvidence,
  Direct4WSideEvidence,
  Item,
  MilestoneSubmittedVia,
  Shipment,
  ShipmentRoute,
  ShipmentRouteMilestone,
  ShipmentRouteStop,
  ShipmentRouteStopCancellation,
  ShipmentRouteStopFailure,
  ShipmentRouteVehicleCheck,
  ShipmentTimelineAttachment,
  ShipmentTimelineEvent,
  StopCancelledTimelineMetadata,
  StopFailedTimelineMetadata,
  shipmentsAPI,
} from '@/services/api/shipments';
import { cancelPackage } from '@/services/api/packages';
import { isFileFieldType } from '@/services/api/stopWorkflows';
import { getProviderNamesByIds } from '@/services/api/providers';
import {
  ITEM_LEG_TYPE_LABELS,
  SHIPMENT_STATUS_LABELS,
  countDeliveryAttempts,
  type ItemDeliveryLeg,
} from '@/services/api/status';
import { getBackendErrorMessage } from '@/utils/errorHandler';
import useFeatureFlag from '@/hooks/useFeatureFlag';
import AuthContext from '@/contexts/AuthContext';
import { canRetryDelivery } from '@/config/ops-permissions';
import RetryDeliveryModal, {
  RetryReasonCode,
} from '@/components/RetryDeliveryModal';
import EditPackageModal, {
  EditablePackage,
} from '@/pages/packages/EditPackageModal';

import CancelShipmentModal from './CancelShipmentModal';
import EditShipmentDestinationModal, {
  canEditShipmentDestination,
} from './EditShipmentDestinationModal';
import CancelStopModal from './CancelStopModal';
import DokumenResmiSection from './DokumenResmiSection';
import InformasiTambahanSection from './InformasiTambahanSection';
import {
  isShipmentLabelPrintable,
  shipmentToPackageRows,
} from '@/components/LabelPreviewModal/shipmentLabels';
import { useShipmentLabels } from '@/components/LabelPreviewModal/useShipmentLabels';
import {
  ItemConditionBadge,
  ItemStatusBadge,
  ShipmentStatusBadge,
} from './ShipmentBadges';
import {
  CANCEL_STOP_COPY,
  CANCEL_STOP_FLAG,
  isNotBilled,
  stopCancelAction,
} from './stopCancel';
import {
  dropFailureSummary,
  isPickupSkipped,
  isRescheduleReason,
  routeEndpoints,
} from './stopFailure';

// Lazy: @react-pdf/renderer is a large dependency and most detail-panel opens
// never print, so it is fetched on the first "Cetak foto" click instead of
// riding along in the main bundle.
const EvidencePrintModal = React.lazy(
  () => import('@/pages/shipments/components/EvidencePrintModal')
);

// Same lifecycle windows as the Packages page (mirrors the backend rules):
// weight/volume editable while still hub-side; cancel only while CREATED.
const EDITABLE_ITEM_STATUSES = ['CREATED', 'HUB_SCANNED'];
const CANCELLABLE_ITEM_STATUSES = ['CREATED'];

interface ShipmentDetailPanelProps {
  /** Shipment id to load. When null the panel is closed. */
  shipmentId: string | null;
  onClose: () => void;
  /**
   * The shipment changed from inside the panel (the E27 Cancel shipment), so
   * a list behind it can update its row.
   */
  onShipmentUpdated?: (shipment: Shipment) => void;
  /** Dash hubs for the "Ubah" dialog's Hub field (partner shipments). */
  hubs?: Pitstop[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Formatters (mirror InvoiceDetailPanel to keep the visual tone consistent)

/** "May 28, 5:11am" — same shape as invoice's activity timestamp. */
function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  const dateStr = d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
  const timeStr = d
    .toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    .toLowerCase()
    .replace(/\s+/g, '');
  return `${dateStr} · ${timeStr}`;
}

function formatCoords(lat: number, lng: number): string {
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
}

function googleMapsHref(lat: number, lng: number): string {
  return `https://www.google.com/maps?q=${lat},${lng}`;
}

function formatWeight(kg: number): string {
  return `${(Number.isFinite(kg) ? kg : 0).toLocaleString(undefined, {
    maximumFractionDigits: 3,
  })} kg`;
}

function formatVolume(m3: number): string {
  return `${(Number.isFinite(m3) ? m3 : 0).toLocaleString(undefined, {
    maximumFractionDigits: 3,
  })} m³`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Panel

const ShipmentDetailPanel: React.FC<ShipmentDetailPanelProps> = ({
  shipmentId,
  onClose,
  onShipmentUpdated,
  hubs,
}) => {
  const [data, setData] = useState<Shipment | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped after an item edit/cancel to re-pull the shipment (fresh item
  // statuses + recomputed totals).
  const [refreshKey, setRefreshKey] = useState(0);
  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!shipmentId) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    shipmentsAPI
      .get(shipmentId)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg =
          err instanceof Error ? err.message : 'Failed to load shipment';
        setError(msg);
        toast.error('Could not load shipment', { description: msg });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [shipmentId, refreshKey]);

  const open = shipmentId !== null;

  // Ops "Cancel stop" (TN2.11) — the whole surface stays hidden until the
  // Remote Config flag is on (after the 4W driver-app force update, D4).
  const cancelStopEnabled = useFeatureFlag(CANCEL_STOP_FLAG, 'boolean');
  const [cancelStopId, setCancelStopId] = useState<string | null>(null);
  const [cancelShipmentOpen, setCancelShipmentOpen] = useState(false);
  const [editDestinationOpen, setEditDestinationOpen] = useState(false);
  const canEditDestination = !!data && canEditShipmentDestination(data);
  // Ops retry of the FAILED delivery behind this shipment — rendered only for
  // the email allowlist (REACT_APP_DELIVERY_RETRY_EMAILS, fail closed); the
  // services re-check everything on the write.
  // useContext, not useAuth: outside a provider (tests) this reads undefined
  // and the allowlist simply denies — same fail-closed behaviour.
  const user = useContext(AuthContext)?.user;
  const [retryOpen, setRetryOpen] = useState(false);
  const [retryLoading, setRetryLoading] = useState(false);
  const canRetry = data?.status === 'FAILED' && canRetryDelivery(user?.email);
  // Looked up on every load so a re-fetch after a 409 feeds the modal fresh data.
  const cancelStop =
    data?.route?.stops.find((s) => s.id === cancelStopId) ?? null;

  useEffect(() => {
    setCancelStopId(null);
    setCancelShipmentOpen(false);
    setEditDestinationOpen(false);
    setRetryOpen(false);
  }, [shipmentId]);

  const handleRetryDelivery = useCallback(
    async (reasonCode: RetryReasonCode, notes: string) => {
      if (!data) return;
      setRetryLoading(true);
      try {
        const res = await shipmentsAPI.retryDelivery(data.id, {
          reasonCode,
          notes,
        });
        toast.success('Delivery dikirim ulang', {
          description: `Delivery ${res.deliveryUid} kembali ke Allocating — shipment & outbound request ter-reset dalam request yang sama.`,
        });
        setRetryOpen(false);
        refetch();
      } catch (err) {
        toast.error('Gagal mengirim ulang delivery', {
          description: err instanceof Error ? err.message : 'Unknown error',
        });
      } finally {
        setRetryLoading(false);
      }
    },
    [data, refetch]
  );

  // Shipping labels from the already-loaded detail — no extra request. Empty
  // (button hidden) for a cancelled shipment or one with no live items.
  const labels = useShipmentLabels();
  const labelRows = useMemo(
    () =>
      data && isShipmentLabelPrintable(data.status)
        ? shipmentToPackageRows(data)
        : [],
    [data]
  );

  // 1E: "N/M drop gagal" — M = drops the driver could fail (not the return leg).
  const dropFailures = data?.route
    ? dropFailureSummary(data.route.stops, routeEndpoints(data))
    : null;

  const titleNode = data ? (
    <span className="flex flex-col gap-2">
      <span className="flex items-center gap-2 flex-wrap">
        <ShipmentStatusBadge status={data.status} />
        {dropFailures && dropFailures.failed > 0 && (
          <Badge
            status="warning"
            size="sm"
            className="tabular-nums"
            data-testid="drop-failure-chip"
          >
            {dropFailures.failed}/{dropFailures.failable} drop gagal
          </Badge>
        )}
        {/* A mixed AWB rolls up to COMPLETED with this flag set — settled, but
            not cleanly delivered. Without it the retur is invisible here. */}
        {data.hasReturn && (
          <Badge status="warning" size="sm">
            Has return
          </Badge>
        )}
        {data.serviceType && (
          <Badge status="neutral" size="sm">
            {data.serviceType}
          </Badge>
        )}
        {/* EXIM direction — only the meaningful lanes; GENERAL/null is not a badge. */}
        {(data.exim === 'IMPORT' || data.exim === 'EXPORT') && (
          <Badge
            status={data.exim === 'IMPORT' ? 'information' : 'feature'}
            size="sm"
          >
            {data.exim === 'IMPORT' ? 'Import' : 'Export'}
          </Badge>
        )}
      </span>
      <span className="font-mono text-sm font-semibold text-text-strong-950 tabular-nums">
        {data.waybill}
      </span>
    </span>
  ) : loading ? (
    'Loading shipment…'
  ) : (
    'Shipment'
  );

  return (
    <FormModal
      open={open}
      onClose={onClose}
      position="right"
      size="md"
      padded={false}
      title={titleNode}
      subtitle={
        data ? (
          <span className="font-mono text-[11px] tabular-nums">
            Booking ID · {data.bookingId}
          </span>
        ) : undefined
      }
      headerExtra={
        labelRows.length > 0 || canRetry || canEditDestination ? (
          <span className="flex items-center gap-2">
            {canEditDestination && (
              <Button
                tone="neutral"
                style="stroke"
                size="xs"
                onClick={() => setEditDestinationOpen(true)}
                data-testid="shipment-detail-edit-destination"
              >
                <i className="ri-edit-line mr-1.5 text-[13px]" />
                Ubah
              </Button>
            )}
            {canRetry && (
              <Button
                tone="primary"
                style="stroke"
                size="xs"
                onClick={() => setRetryOpen(true)}
                data-testid="shipment-detail-retry-delivery"
              >
                <i className="ri-restart-line mr-1.5 text-[13px]" />
                Retry delivery
              </Button>
            )}
            {labelRows.length > 0 && (
              <Button
                tone="neutral"
                style="stroke"
                size="xs"
                onClick={() => labels.open(labelRows)}
                data-testid="shipment-detail-print-label"
              >
                <i className="ri-printer-line mr-1.5 text-[13px]" />
                Print label
              </Button>
            )}
          </span>
        ) : undefined
      }
    >
      <div className="px-5 py-5">
        {loading && !data && <DetailSkeleton />}
        {error && !loading && (
          <div className="rounded-lg border border-error-light bg-error-lighter px-3 py-2 text-xs text-error-base">
            {error}
          </div>
        )}
        {data && (
          <div className="space-y-7">
            <DetailsSection shipment={data} />
            <PartiesSection shipment={data} />
            <RouteSection shipment={data} />
            {/* For 4W the pair replaces the generic section — same photos,
                and rendering both would duplicate every tile. */}
            {data.direct4wEvidence ? (
              <Direct4WEvidenceSection
                evidence={data.direct4wEvidence}
                route={data.route}
                shipment={data}
              />
            ) : (
              <EvidenceSection route={data.route} />
            )}
            {cancelStopEnabled &&
              data.type === 'DIRECT_4W' &&
              data.route &&
              data.route.stops.length > 0 && (
                <RouteStopsSection
                  route={data.route}
                  onCancel={(stop) => setCancelStopId(stop.id)}
                />
              )}
            {data.type === 'DIRECT_4W' && (
              <DokumenResmiSection
                shipmentId={data.id}
                clientName={data.client?.name}
                exim={data.exim}
                documents={data.officialDocuments}
                onUpdated={(docs) =>
                  setData((prev) =>
                    prev ? { ...prev, officialDocuments: docs } : prev
                  )
                }
              />
            )}
            {data.type === 'DIRECT_4W' && (
              <InformasiTambahanSection
                shipmentId={data.id}
                clientName={data.client?.name}
                exim={data.exim}
                notes={data.additionalNotes}
                onUpdated={(notes) =>
                  setData((prev) =>
                    prev ? { ...prev, additionalNotes: notes } : prev
                  )
                }
              />
            )}
            <TimelineSection events={data.timeline} />
            <ItemsSection items={data.items ?? []} onChanged={refetch} />
            {data.status === 'CANCELLED' && (
              <CancellationSection shipment={data} />
            )}

            {data.route && (
              <CancelStopModal
                shipment={data}
                route={data.route}
                stop={cancelStopEnabled ? cancelStop : null}
                onClose={() => setCancelStopId(null)}
                onCancelled={(result) => {
                  const cascaded = result.cascadedStopIds?.length ?? 0;
                  toast.success(`Stop ${result.sequence} cancelled`, {
                    description:
                      cascaded > 0
                        ? `${CANCEL_STOP_COPY.explanation} ${cascaded} dependent stop${
                            cascaded === 1 ? '' : 's'
                          } cancelled with it.`
                        : CANCEL_STOP_COPY.explanation,
                  });
                  setCancelStopId(null);
                  refetch();
                }}
                onRefresh={refetch}
                onCancelShipment={() => {
                  setCancelStopId(null);
                  setCancelShipmentOpen(true);
                }}
              />
            )}
            {labels.modal}
            <RetryDeliveryModal
              isOpen={retryOpen}
              onClose={() => setRetryOpen(false)}
              onConfirm={handleRetryDelivery}
              isLoading={retryLoading}
            />
            <EditShipmentDestinationModal
              shipment={editDestinationOpen ? data : null}
              hubs={hubs}
              onClose={() => setEditDestinationOpen(false)}
              onSaved={(updated) => {
                setEditDestinationOpen(false);
                onShipmentUpdated?.(updated);
                refetch();
              }}
            />
            <CancelShipmentModal
              shipment={cancelShipmentOpen ? data : null}
              onClose={() => setCancelShipmentOpen(false)}
              onCancelled={(updated) => {
                setCancelShipmentOpen(false);
                onShipmentUpdated?.(updated);
                refetch();
              }}
            />
          </div>
        )}
      </div>
    </FormModal>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Reusable layout primitives (mirror invoice)

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <h4 className="mb-3 text-sm font-bold tracking-tight text-text-strong-950">
    {children}
  </h4>
);

interface RowProps {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  mono?: boolean;
  tag?: React.ReactNode;
}

const Row: React.FC<RowProps> = ({ label, value, sub, mono, tag }) => (
  <div className="flex items-start justify-between gap-4 py-2.5">
    <dt className="text-[12px] text-text-sub-600 shrink-0 pt-0.5">{label}</dt>
    <dd className="text-right min-w-0">
      <div className="flex items-center justify-end gap-2">
        {tag}
        <span
          className={`text-[13px] text-text-strong-950 tracking-tight ${
            mono ? 'font-mono tabular-nums' : ''
          }`}
        >
          {value}
        </span>
      </div>
      {sub && (
        <div className="mt-0.5 text-[11px] text-text-sub-600 leading-snug">
          {sub}
        </div>
      )}
    </dd>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Details — high-level shipment metadata

const DetailsSection: React.FC<{ shipment: Shipment }> = ({ shipment }) => {
  const clientName = shipment.client?.name?.trim() || `#${shipment.clientId}`;
  // 4W is a point-to-point trip, not a parcel shipment — the item/koli/weight/
  // volume rollups are always zero and just noise, so they are hidden and the
  // driver + vehicle snapshot is shown instead.
  const is4W = shipment.type === 'DIRECT_4W';

  // External provider is stored as an id; resolve it to the provider NAME.
  const [providerName, setProviderName] = useState<string | null>(null);
  useEffect(() => {
    const id = shipment.externalProviderId;
    if (id == null) {
      setProviderName(null);
      return;
    }
    let cancelled = false;
    getProviderNamesByIds([id])
      .then((names) => {
        if (!cancelled) setProviderName(names[id] ?? null);
      })
      .catch(() => {
        /* leave null — falls back to #id */
      });
    return () => {
      cancelled = true;
    };
  }, [shipment.externalProviderId]);

  return (
    <section>
      <SectionTitle>Details</SectionTitle>
      <dl className="divide-y divide-stroke-soft-200 border-y border-stroke-soft-200">
        <Row
          label="Client"
          value={clientName}
          sub={shipment.revenueStream ?? undefined}
        />
        <Row label="Service" value={shipment.serviceType} />
        {shipment.deliveryGroupId && (
          <Row label="Delivery group" value={shipment.deliveryGroupId} mono />
        )}
        {/* Parcel rollups — hidden for 4W (always zero on a trip). Completeness
            is a count in v2, never a status. */}
        {!is4W && (
          <>
            <Row
              label="Items"
              value={shipment.itemCount ?? shipment.items?.length ?? 0}
              mono
              sub={
                shipment.itemsSummary
                  ? `${shipment.itemsSummary.scanned} / ${shipment.itemsSummary.total} scanned`
                  : undefined
              }
            />
            {/* Summed parcels across the shipment's items. Absent on older BE builds. */}
            {shipment.totalKoli != null && (
              <Row label="Total koli" value={shipment.totalKoli} mono />
            )}
            <Row
              label="Total weight"
              value={formatWeight(shipment.totalWeight)}
              mono
            />
            <Row
              label="Total volume"
              value={formatVolume(shipment.totalVolume)}
              mono
            />
          </>
        )}
        <Row
          label="External provider"
          value={providerName ?? `#${shipment.externalProviderId}`}
          sub={
            shipment.externalShiftId
              ? `Shift ${shipment.externalShiftId}`
              : undefined
          }
        />
        {/* Driver + vehicle snapshot frozen on the 4W route. */}
        {is4W && (
          <>
            <Row
              label="Driver code"
              value={shipment.route?.driverCode || '—'}
              mono
            />
            <Row label="Driver name" value={shipment.route?.driverName || '—'} />
            <Row
              label="Plat nomor"
              value={shipment.route?.vehicle?.plateNumber || '—'}
              mono
            />
            <Row
              label="Jenis unit"
              value={shipment.route?.vehicle?.unitType || '—'}
            />
          </>
        )}
        <Row label="Upload shipment" value={shipment.uploadShipmentId} mono />
        <Row label="Created" value={formatDateTime(shipment.createdAt)} mono />
        {shipment.dispatchedAt && (
          <Row
            label="Dispatched"
            value={formatDateTime(shipment.dispatchedAt)}
            mono
          />
        )}
        {/* Recon notes moved to the "Informasi Tambahan" section below, which
            renders the same values PLUS the slots ops can still fill. Listing
            them here too would show each filled value twice. */}
      </dl>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Parties — sender + receiver

const PartiesSection: React.FC<{ shipment: Shipment }> = ({ shipment }) => (
  <section>
    <SectionTitle>Parties</SectionTitle>
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <PartyCard
        label="Sender"
        name={shipment.senderName}
        phone={shipment.senderPhone}
      />
      <PartyCard
        label="Receiver"
        name={shipment.receiverName}
        phone={shipment.receiverPhone}
      />
    </div>
  </section>
);

const PartyCard: React.FC<{
  label: string;
  name: string;
  phone: string | null;
}> = ({ label, name, phone }) => (
  <div className="rounded-lg border border-stroke-soft-200 bg-white px-3 py-2.5">
    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
      {label}
    </p>
    <p className="mt-1.5 text-[13px] font-semibold tracking-tight text-text-strong-950">
      {name || '—'}
    </p>
    {phone && (
      <p className="mt-0.5 font-mono text-[11px] tabular-nums text-text-sub-600">
        {phone}
      </p>
    )}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Route — origin + destination as paired stops

const RouteSection: React.FC<{ shipment: Shipment }> = ({ shipment }) => (
  <section>
    <SectionTitle>Route</SectionTitle>
    <div className="space-y-3">
      <StopCard
        kind="origin"
        name={shipment.senderName}
        address={shipment.originAddress}
        lat={shipment.originLat}
        lng={shipment.originLong}
        h3={shipment.originH3Index}
        phone={shipment.originPhone}
      />
      <StopCard
        kind="destination"
        name={shipment.receiverName}
        address={shipment.destinationAddress}
        lat={shipment.destinationLat}
        lng={shipment.destinationLong}
        h3={shipment.destinationH3Index}
        phone={shipment.destinationPhone}
      />
    </div>
  </section>
);

const StopCard: React.FC<{
  kind: 'origin' | 'destination';
  // Contact behind the stop: origin = senderName, destination = receiverName.
  // Shown as the headline above the address.
  name: string | null;
  // All nullable: H2H origin (e.g. ZALORA) arrives with no address / coords /
  // h3 at all — the pickup is only resolved later at inbound scan. Every field
  // renders a fallback instead of assuming it's present.
  address: string | null;
  lat: number | null;
  lng: number | null;
  h3: string | null;
  phone: string | null;
}> = ({ kind, name, address, lat, lng, h3, phone }) => {
  const isOrigin = kind === 'origin';
  const hasCoords = lat != null && lng != null;
  return (
    <div className="rounded-lg border border-stroke-soft-200 bg-white px-3 py-2.5">
      <div className="flex items-start gap-2">
        <span
          className={`mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full ${
            isOrigin
              ? 'bg-(--theme-accent-alpha-10) text-accent'
              : 'bg-(--state-success-lighter) text-(--state-success-base)'
          }`}
        >
          <i
            className={`text-[12px] ${
              isOrigin ? 'ri-circle-line' : 'ri-map-pin-fill'
            }`}
            aria-hidden="true"
          />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
            {isOrigin ? 'Origin' : 'Destination'}
          </p>
          {name ? (
            <>
              <p className="mt-1 text-[13px] font-medium text-text-strong-950 leading-snug">
                {name}
              </p>
              <p className="mt-0.5 text-[12px] text-text-sub-600 leading-snug">
                {address || '—'}
              </p>
            </>
          ) : (
            <p className="mt-1 text-[13px] text-text-strong-950 leading-snug">
              {address || '—'}
            </p>
          )}
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
            {hasCoords ? (
              <a
                href={googleMapsHref(lat, lng)}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-[11px] tabular-nums text-accent hover:underline"
              >
                {formatCoords(lat, lng)}
                <i
                  className="ri-external-link-line ml-1 text-[12px]"
                  aria-hidden="true"
                />
              </a>
            ) : (
              <span className="font-mono text-[11px] tabular-nums text-text-soft-400">
                No coordinates
              </span>
            )}
            {h3 && (
              <span className="font-mono text-[10px] tabular-nums text-text-soft-400">
                h3 · {h3}
              </span>
            )}
            {phone && (
              <span className="font-mono text-[11px] tabular-nums text-text-sub-600">
                {phone}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Retur — why the recipient refused the parcel, captured by the rider app

/**
 * Retur details, only ever set on the retur branch. `returReasonCode` is a code
 * from the reason taxonomy (`GET /v1/reasons`); we show it raw rather than
 * fetching the taxonomy just to label one row.
 */
const ReturNote: React.FC<{ item: Item }> = ({ item }) => {
  if (
    !item.returReasonCode &&
    !item.returReasonNote &&
    item.returWeight == null
  )
    return null;

  return (
    <div className="mt-2 rounded-md border border-(--state-warning-light) bg-(--state-warning-lighter) px-2.5 py-2">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-(--state-warning-dark)">
        Retur
      </p>
      <p className="mt-1 text-[11px] text-text-sub-600 leading-snug">
        {item.returReasonCode && (
          <span className="font-mono tabular-nums text-text-strong-950">
            {item.returReasonCode}
          </span>
        )}
        {item.returWeight != null && (
          <>
            {item.returReasonCode && (
              <span className="mx-1.5 text-text-soft-400">·</span>
            )}
            <span className="font-mono tabular-nums">
              {formatWeight(item.returWeight)}
            </span>
          </>
        )}
      </p>
      {item.returReasonNote && (
        <p className="mt-1 text-[11px] text-text-sub-600 leading-snug">
          {item.returReasonNote}
        </p>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Legs — every delivery trip this item took

/**
 * `item_delivery_legs`, oldest first. This is what replaced the single
 * `externalDeliveryUid` column: a failed attempt keeps its leg and the retry
 * appends a new one, so the number of attempts is readable, and the DE-1 uid
 * survives a retur instead of being overwritten.
 */
const ItemLegs: React.FC<{ legs?: ItemDeliveryLeg[] }> = ({ legs }) => {
  if (!legs || legs.length === 0) return null;
  const attempts = countDeliveryAttempts(legs);

  return (
    <div className="mt-2 rounded-md border border-stroke-soft-200 bg-bg-weak-50 px-2.5 py-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
          Deliveries
        </p>
        {attempts > 1 && (
          <span className="font-mono text-[10px] tabular-nums text-(--state-warning-dark)">
            {attempts} attempts
          </span>
        )}
      </div>
      <ul className="mt-1.5 space-y-1">
        {legs.map((leg) => (
          <li
            key={leg.id}
            className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px]"
          >
            <span className="font-mono tabular-nums text-text-soft-400">
              #{leg.legSeq}
            </span>
            <span className="text-text-sub-600">
              {ITEM_LEG_TYPE_LABELS[leg.legType] ?? leg.legType}
            </span>
            <span className="font-mono tabular-nums text-text-strong-950">
              {leg.deliveryUid}
            </span>
            {leg.deliveryStatus && (
              <span className="font-mono text-[10px] text-text-sub-600">
                {leg.deliveryStatus}
              </span>
            )}
            {leg.isActive && (
              <Badge status="feature" size="sm">
                Active
              </Badge>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Driver evidence — 4W milestone photos captured on the route

const STOP_TYPE_LABELS: Record<string, string> = {
  PICKUP: 'Pickup',
  DROP_OFF: 'Drop-off',
};

const EVIDENCE_STATUS_BADGE: Record<
  string,
  'success' | 'neutral' | 'error' | 'warning'
> = {
  COMPLETED: 'success',
  PENDING: 'neutral',
  FAILED: 'error',
  SKIPPED: 'warning',
  // An Ops cancel is a recorded decision, not a failure.
  CANCELLED: 'neutral',
};

type PhotoTilesVariant = 'photo' | 'signature';

/**
 * Tile box per variant. A signature is 2:1 like the pad export (1200×600) and
 * sits on fixed white in both themes, so black ink never vanishes on a dark
 * surface and an ops photo of the delivery note is shown whole, not cropped.
 */
const TILE_BOX: Record<PhotoTilesVariant, string> = {
  photo: 'h-14 w-14 bg-tint',
  signature: 'h-14 w-28 bg-static-white',
};

/** A signature field's label, with the design's fallback when it is blank. */
const signatureLabel = (label: string | null | undefined): string =>
  label?.trim() || 'Tanda tangan';

/**
 * "Filled by ops" beside a signature tile (design 5B): the stop was closed via
 * Mark completed, so the tile is a photo of the signed delivery note rather
 * than a signature drawn on the rider app.
 */
const OpsSignatureBadge: React.FC = () => (
  <Badge status="warning" size="sm">
    Diisi ops · foto surat jalan
  </Badge>
);

/**
 * Badge text for the known stop statuses (English, G9). An unknown status
 * shows its raw value (contract §1) and never gets an action.
 */
const EVIDENCE_STATUS_LABEL: Record<string, string> = {
  COMPLETED: 'Completed',
  PENDING: 'Pending',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
  SKIPPED: 'Skipped',
};

const StopStatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <Badge status={EVIDENCE_STATUS_BADGE[status] ?? 'neutral'} size="sm">
    {EVIDENCE_STATUS_LABEL[status] ?? status}
  </Badge>
);

/**
 * A stop closed without being worked through — failed by the driver,
 * cancelled by Ops, or skipped because its pickup failed. Its unreached
 * milestones were SKIPPED; only the ones actually sent stay on the card (1D).
 */
const closedEarly = (
  stop: Pick<ShipmentRouteStop, 'status' | 'skipReason'> | null
): boolean =>
  !!stop &&
  (stop.status === 'FAILED' ||
    stop.status === 'CANCELLED' ||
    isPickupSkipped(stop));

const visibleMilestones = (
  stop: Pick<ShipmentRouteStop, 'status' | 'skipReason'> | null,
  milestones: ShipmentRouteMilestone[]
): ShipmentRouteMilestone[] =>
  closedEarly(stop)
    ? milestones.filter((m) => m.status === 'COMPLETED')
    : milestones;

/**
 * Photo tiles with a click-to-zoom lightbox (mirrors the delivery timeline).
 * `variant="signature"` draws a 112×56 `object-contain` tile on white (design
 * 5A) with the same lightbox, focus and hover — and no overlay stamp (5C): the
 * time is on the milestone row and the real stamp is in the image's band.
 * `badge` renders beside the tiles. `altPrefix` names what photo tiles prove
 * ("Failure photo · Stop 3 · …"); each tile appends its number.
 */
const PhotoTiles: React.FC<{
  urls: string[];
  variant?: PhotoTilesVariant;
  /** Alt text base for signature tiles — the field label. */
  label?: string;
  badge?: React.ReactNode;
  /** Alt text base for photo tiles; each tile appends its number. */
  altPrefix?: string;
}> = ({ urls, variant = 'photo', label, badge, altPrefix = 'Foto' }) => {
  const [open, setOpen] = useState<number | null>(null);
  const isSignature = variant === 'signature';
  const altFor = (i: number): string => {
    if (!isSignature) return `${altPrefix} ${i + 1}`;
    const name = signatureLabel(label);
    return urls.length > 1 ? `${name} ${i + 1}` : name;
  };
  if (urls.length === 0) {
    return (
      <p className="mt-1 text-[11px] italic text-text-sub-600">
        Belum ada foto
      </p>
    );
  }
  return (
    <>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        {urls.map((url, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setOpen(i)}
            className={`${TILE_BOX[variant]} overflow-hidden rounded-gsm-sm border border-stroke-soft-200 transition-all hover:ring-2 hover:ring-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60`}
          >
            <img
              src={url}
              alt={altFor(i)}
              className={`h-full w-full ${
                isSignature ? 'object-contain' : 'object-cover'
              }`}
              loading="lazy"
            />
          </button>
        ))}
        {badge}
      </div>

      {open !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink/85 p-6"
          onClick={() => setOpen(null)}
        >
          <img
            src={urls[open]}
            alt={altFor(open)}
            className="max-h-full max-w-full rounded-gsm object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          {urls.length > 1 && (
            <>
              {open > 0 && (
                <button
                  type="button"
                  aria-label="Previous"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen((i) => (i ?? 0) - 1);
                  }}
                  className="absolute left-6 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25"
                >
                  <i className="ri-arrow-left-s-line text-2xl" />
                </button>
              )}
              {open < urls.length - 1 && (
                <button
                  type="button"
                  aria-label="Next"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen((i) => (i ?? 0) + 1);
                  }}
                  className="absolute right-6 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25"
                >
                  <i className="ri-arrow-right-s-line text-2xl" />
                </button>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

/**
 * A milestone's name, submit time and photo tiles. Shared by the per-stop
 * evidence card and the DIRECT_4W start/end pair — same data, same rendering.
 * A SIGNATURE field always carries its label (a bare 2:1 tile does not say
 * whose signature it is) and the ops badge when ops submitted the milestone.
 */
const MilestonePhotos: React.FC<{ milestones: ShipmentRouteMilestone[] }> = ({
  milestones,
}) => (
  <>
    {milestones.map((m) => (
      <div key={m.key}>
        <div className="flex items-center justify-between gap-2">
          <span className="text-[12px] font-semibold text-text-strong-950">
            {m.name}
          </span>
          {m.submittedAt && (
            <span className="text-[11px] tabular-nums text-text-sub-600">
              {formatDateTime(m.submittedAt)}
            </span>
          )}
        </div>
        {m.photoFields.map((f) => {
          const isSignature = f.type === 'SIGNATURE';
          return (
            <div key={f.fieldKey} className="mt-1">
              {(isSignature || m.photoFields.length > 1) && (
                <p className="text-[11px] text-text-sub-600">
                  {isSignature ? signatureLabel(f.label) : f.label}
                </p>
              )}
              <PhotoTiles
                urls={f.urls}
                variant={isSignature ? 'signature' : 'photo'}
                label={f.label}
                badge={
                  isSignature && m.submittedVia === 'OPS' ? (
                    <OpsSignatureBadge />
                  ) : undefined
                }
              />
            </div>
          );
        })}
      </div>
    ))}
  </>
);

/** Shared shell of a stop-outcome block: tone, icon, title row, body. */
const OUTCOME_TONE = {
  error: {
    box: 'border-error-light bg-error-lighter',
    icon: 'text-error-base',
  },
  warning: {
    box: 'border-(--state-warning-light) bg-(--state-warning-lighter)',
    icon: 'text-(--state-warning-dark)',
  },
  neutral: {
    box: 'border-stroke-soft-200 bg-bg-weak-50',
    icon: 'text-text-sub-600',
  },
} as const;

const StopOutcomeShell: React.FC<{
  testId: string;
  tone: keyof typeof OUTCOME_TONE;
  icon: string;
  title: React.ReactNode;
  badges?: React.ReactNode;
  children?: React.ReactNode;
}> = ({ testId, tone, icon, title, badges, children }) => (
  <div
    data-testid={testId}
    data-tone={tone}
    className={`mt-2.5 rounded-gsm border p-2.5 ${OUTCOME_TONE[tone].box}`}
  >
    <div className="flex flex-wrap items-start justify-between gap-1.5">
      <p className="flex items-start gap-1.5 text-[12px] font-semibold text-text-strong-950">
        <i
          aria-hidden
          className={`${icon} mt-px text-[13px] leading-none ${OUTCOME_TONE[tone].icon}`}
        />
        <span>{title}</span>
      </p>
      {badges && (
        <span className="flex flex-wrap items-center gap-1">{badges}</span>
      )}
    </div>
    {children}
  </div>
);

/** "Penerima minta datang lagi" — EC5009 / EB3007, Ops reschedules (TN2.1). */
const RESCHEDULE_LABEL = 'Penerima minta datang lagi';

/**
 * Why the driver failed a stop (1D, TN2.1): "Failed by driver · <reason>",
 * note, when + who, and the proof photos. Text + icon, never colour alone
 * (6A); a reason with no photo simply has no tile row. A reschedule reason
 * (EC5009 / EB3007) reads "Penerima minta datang lagi" in the warning tone, so
 * it stands apart from a terminal failure. A fail that landed on a stop Ops
 * had cancelled says so (E26: the driver record wins).
 */
const StopFailureBlock: React.FC<{
  sequence: number;
  failure: ShipmentRouteStopFailure;
}> = ({ sequence, failure }) => {
  const actor = formatActor(failure.failedBy);
  const reschedule = isRescheduleReason(failure.reasonCode);
  return (
    <StopOutcomeShell
      testId={`stop-failure-${sequence}`}
      tone={reschedule ? 'warning' : 'error'}
      icon={reschedule ? 'ri-time-line' : 'ri-close-circle-line'}
      title={`Failed by driver · ${failure.reasonLabel}`}
      badges={
        <>
          {reschedule && (
            <Badge status="warning" size="sm">
              {RESCHEDULE_LABEL}
            </Badge>
          )}
          {failure.overridesCancellation && (
            <Badge status="neutral" size="sm">
              Overrides Ops cancel
            </Badge>
          )}
        </>
      }
    >
      {failure.note && (
        <p className="mt-1 text-[11px] leading-snug text-text-strong-950">
          {failure.note}
        </p>
      )}
      <p className="mt-1 text-[11px] tabular-nums text-text-sub-600">
        {formatDateTime(failure.failedAt)}
        {actor ? ` · ${actor}` : ''}
      </p>
      {failure.photoUrls.length > 0 && (
        <PhotoTiles
          urls={failure.photoUrls}
          altPrefix={`Failure photo · Stop ${sequence} · ${failure.reasonLabel}`}
        />
      )}
    </StopOutcomeShell>
  );
};

/**
 * Why Ops cancelled a stop (TN2.1): "Cancelled by Ops · <reason>" (the label
 * stays Indonesian), note, when + who, and "Not billed" for an internal input
 * error. Neutral tone: a cancel is a decision, not a failure.
 */
const StopCancellationBlock: React.FC<{
  sequence: number;
  cancellation: ShipmentRouteStopCancellation | null | undefined;
}> = ({ sequence, cancellation }) => {
  const actor = formatActor(cancellation?.cancelledBy ?? null);
  return (
    <StopOutcomeShell
      testId={`stop-cancellation-${sequence}`}
      tone="neutral"
      icon="ri-forbid-2-line"
      title={
        cancellation?.reasonLabel
          ? `Cancelled by Ops · ${cancellation.reasonLabel}`
          : 'Cancelled by Ops'
      }
      badges={
        isNotBilled(cancellation?.category) ? (
          <Badge status="information" size="sm">
            {CANCEL_STOP_COPY.notBilled}
          </Badge>
        ) : undefined
      }
    >
      {cancellation?.note && (
        <p className="mt-1 text-[11px] leading-snug text-text-strong-950">
          {cancellation.note}
        </p>
      )}
      {cancellation && (
        <p className="mt-1 text-[11px] tabular-nums text-text-sub-600">
          {formatDateTime(cancellation.cancelledAt)}
          {actor ? ` · ${actor}` : ''}
        </p>
      )}
    </StopOutcomeShell>
  );
};

/** A drop the server skipped because its single pickup failed (contract §1). */
const StopPickupSkippedBlock: React.FC<{ sequence: number }> = ({
  sequence,
}) => (
  <StopOutcomeShell
    testId={`stop-skipped-${sequence}`}
    tone="neutral"
    icon="ri-skip-forward-line"
    title="Skipped · pickup failed"
  />
);

/** Whatever closed the stop early, as its block; null for any other stop. */
const StopOutcomeBlock: React.FC<{ stop: ShipmentRouteStop }> = ({ stop }) => {
  if (stop.status === 'FAILED' && stop.failure) {
    return <StopFailureBlock sequence={stop.sequence} failure={stop.failure} />;
  }
  if (stop.status === 'CANCELLED') {
    return (
      <StopCancellationBlock
        sequence={stop.sequence}
        cancellation={stop.cancellation}
      />
    );
  }
  if (isPickupSkipped(stop)) {
    return <StopPickupSkippedBlock sequence={stop.sequence} />;
  }
  return null;
};

const EvidenceStopCard: React.FC<{ stop: ShipmentRouteStop }> = ({ stop }) => {
  const milestones = visibleMilestones(stop, stop.milestones);
  return (
    <div
      data-testid={`evidence-stop-${stop.sequence}`}
      className="rounded-gsm border border-stroke-soft-200 bg-bg-white-0 p-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="text-[12px] font-bold tracking-tight text-text-strong-950">
            {stop.sequence}. {STOP_TYPE_LABELS[stop.type] ?? stop.type}
          </span>
          <p className="mt-0.5 text-[11px] leading-snug text-text-sub-600">
            {stop.address}
          </p>
        </div>
        <StopStatusBadge status={stop.status} />
      </div>

      <StopOutcomeBlock stop={stop} />

      {milestones.length > 0 && (
        <div className="mt-2.5 space-y-2.5 border-t border-stroke-soft-200 pt-2.5">
          <MilestonePhotos milestones={milestones} />
        </div>
      )}
    </div>
  );
};

/**
 * A stop with an outcome to show even without photos: failed by the driver
 * (with a record), cancelled by Ops, or skipped because its pickup failed.
 */
const hasStopOutcome = (stop: ShipmentRouteStop): boolean =>
  (stop.status === 'FAILED' && !!stop.failure) ||
  stop.status === 'CANCELLED' ||
  isPickupSkipped(stop);

/** One vehicle-condition phase (start/end): odometer reading + labelled photos. */
const VehicleCheckCard: React.FC<{
  title: string;
  check: ShipmentRouteVehicleCheck;
}> = ({ title, check }) => (
  <div className="rounded-gsm border border-stroke-soft-200 bg-bg-white-0 p-3">
    <div className="flex items-center justify-between gap-3">
      <span className="text-[12px] font-bold tracking-tight text-text-strong-950">
        {title}
      </span>
      {check.submittedAt && (
        <span className="text-[11px] tabular-nums text-text-sub-600">
          {formatDateTime(check.submittedAt)}
        </span>
      )}
    </div>
    <p className="mt-0.5 text-[11px] text-text-sub-600">
      Odometer:{' '}
      <span className="font-mono tabular-nums text-text-strong-950">
        {check.odometerReading.toLocaleString('id-ID')} km
      </span>
    </p>
    <div className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1.5 border-t border-stroke-soft-200 pt-2.5 sm:grid-cols-3">
      {check.photoFields.map((f) => (
        <div key={f.fieldKey}>
          <p className="text-[11px] text-text-sub-600">{f.label}</p>
          <PhotoTiles urls={f.urls} />
        </div>
      ))}
    </div>
  </div>
);

const EvidenceSection: React.FC<{ route?: ShipmentRoute | null }> = ({
  route,
}) => {
  const stops = (route?.stops ?? []).filter(
    (s) => visibleMilestones(s, s.milestones).length > 0 || hasStopOutcome(s)
  );
  const startCheck = route?.startCheck ?? null;
  const endCheck = route?.endCheck ?? null;
  if (!route || (stops.length === 0 && !startCheck && !endCheck)) return null;
  return (
    <section>
      <SectionTitle>Driver evidence</SectionTitle>
      <p className="-mt-1 mb-3 text-[11px] text-text-sub-600">
        4W route <span className="font-mono">{route.code}</span> · photos taken
        by the driver during the trip.
      </p>
      <div className="space-y-2.5">
        {startCheck && (
          <VehicleCheckCard
            title="Kondisi kendaraan · awal"
            check={startCheck}
          />
        )}
        {stops.map((stop) => (
          <EvidenceStopCard key={stop.id} stop={stop} />
        ))}
        {endCheck && (
          <VehicleCheckCard
            title="Kondisi kendaraan · akhir"
            check={endCheck}
          />
        )}
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Route stops — every stop with its status and the Ops "Cancel stop" action

/**
 * Every stop of the route in order, with the Ops "Cancel stop" action (PRD
 * TN2.11). Rendered only behind CANCEL_STOP_FLAG. The server decides what is
 * possible (`cancellable`, `opened`, `isReturnToOrigin`); `stopCancelAction`
 * only picks the presentation: an enabled button, a disabled one with the
 * "driver already arrived" hint, the return-to-origin hint, or nothing for a
 * terminal or unknown status.
 */
const RouteStopsSection: React.FC<{
  route: ShipmentRoute;
  onCancel: (stop: ShipmentRouteStop) => void;
}> = ({ route, onCancel }) => {
  const stops = [...route.stops].sort((a, b) => a.sequence - b.sequence);
  return (
    <section data-testid="route-stops">
      <SectionTitle>
        Route stops · {stops.length} {stops.length === 1 ? 'stop' : 'stops'}
      </SectionTitle>
      <ul className="divide-y divide-stroke-soft-200 overflow-hidden rounded-lg border border-stroke-soft-200">
        {stops.map((stop) => {
          const action = stopCancelAction(stop);
          const hintId = `route-stop-hint-${stop.id}`;
          return (
            <li
              key={stop.id}
              data-testid={`route-stop-${stop.sequence}`}
              className="bg-bg-white-0 px-3 py-2.5"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <span className="text-[12px] font-bold tracking-tight text-text-strong-950">
                    {stop.sequence}. {STOP_TYPE_LABELS[stop.type] ?? stop.type}
                  </span>
                  <p className="mt-0.5 text-[11px] leading-snug text-text-sub-600">
                    {stop.address}
                  </p>
                </div>
                <StopStatusBadge status={stop.status} />
              </div>
              {(action === 'CANCEL' || action === 'OPENED') && (
                <div className="mt-2 space-y-1">
                  <Button
                    tone="destructive"
                    style="stroke"
                    size="xs"
                    disabled={action === 'OPENED'}
                    aria-label={`${CANCEL_STOP_COPY.action} ${stop.sequence}`}
                    aria-describedby={action === 'OPENED' ? hintId : undefined}
                    onClick={() => onCancel(stop)}
                  >
                    <i className="ri-forbid-2-line mr-1" />
                    {CANCEL_STOP_COPY.action}
                  </Button>
                  {action === 'OPENED' && (
                    <p
                      id={hintId}
                      className="text-[11px] leading-snug text-text-sub-600"
                    >
                      {CANCEL_STOP_COPY.openedHint}
                    </p>
                  )}
                </div>
              )}
              {action === 'RETURN_TO_ORIGIN' && (
                <p className="mt-2 text-[11px] leading-snug text-text-sub-600">
                  {CANCEL_STOP_COPY.returnToOriginHint}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// DIRECT_4W — the trip's two ends, as their own pair

/**
 * One end of the trip. Every stop field can be null (BE surfaces a vehicle
 * check even before its stop resolves), so the header degrades to the bare
 * label rather than rendering "null".
 */
const Direct4WSideCard: React.FC<{
  label: string;
  side: Direct4WSideEvidence;
  /**
   * The same stop on the route — the side does not carry its failure, Ops
   * cancel or skip reason. Null when the route does not list the stop.
   */
  stop?: ShipmentRouteStop | null;
}> = ({ label, side, stop }) => {
  const stopLabel = side.type
    ? (STOP_TYPE_LABELS[side.type] ?? side.type)
    : null;
  const timestamp = side.resolvedAt ?? side.arrivedAt;
  const milestones = visibleMilestones(
    stop ?? (side.status ? { status: side.status } : null),
    side.milestones
  );
  const showOutcome = !!stop && hasStopOutcome(stop);
  const isEmpty = milestones.length === 0 && !side.vehicleCheck && !showOutcome;
  return (
    <div className="rounded-gsm border border-stroke-soft-200 bg-bg-white-0 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="text-[12px] font-bold tracking-tight text-text-strong-950">
            {label}
            {stopLabel ? ` · ${stopLabel}` : ''}
          </span>
          {side.address && (
            <p className="mt-0.5 text-[11px] leading-snug text-text-sub-600">
              {side.address}
            </p>
          )}
          {timestamp && (
            <p className="mt-0.5 text-[11px] tabular-nums text-text-sub-600">
              {formatDateTime(timestamp)}
            </p>
          )}
        </div>
        {side.status && <StopStatusBadge status={side.status} />}
      </div>

      {showOutcome && stop && <StopOutcomeBlock stop={stop} />}

      {isEmpty ? (
        <p className="mt-2.5 border-t border-stroke-soft-200 pt-2.5 text-[11px] italic text-text-sub-600">
          Belum ada evidence
        </p>
      ) : milestones.length === 0 && !side.vehicleCheck ? null : (
        <div className="mt-2.5 space-y-2.5 border-t border-stroke-soft-200 pt-2.5">
          <MilestonePhotos milestones={milestones} />

          {side.vehicleCheck && (
            <div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[12px] font-semibold text-text-strong-950">
                  Kondisi kendaraan
                </span>
                {side.vehicleCheck.submittedAt && (
                  <span className="text-[11px] tabular-nums text-text-sub-600">
                    {formatDateTime(side.vehicleCheck.submittedAt)}
                  </span>
                )}
              </div>
              <p className="mt-0.5 text-[11px] text-text-sub-600">
                Odometer:{' '}
                <span className="font-mono tabular-nums text-text-strong-950">
                  {side.vehicleCheck.odometerReading.toLocaleString('id-ID')} km
                </span>
              </p>
              <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1.5 sm:grid-cols-3">
                {side.vehicleCheck.photoFields.map((f) => (
                  <div key={f.fieldKey}>
                    <p className="text-[11px] text-text-sub-600">{f.label}</p>
                    <PhotoTiles urls={f.urls} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * DIRECT_4W reads as a point-to-point trip, so BE hands us its two ends as a
 * pair (`direct4wEvidence`) instead of a stop list. When it is present it
 * REPLACES the generic Driver-evidence section — same photos, better shape;
 * rendering both would show every photo twice.
 */
const Direct4WEvidenceSection: React.FC<{
  evidence?: Direct4WEvidence | null;
  route?: ShipmentRoute | null;
  shipment: Shipment;
}> = ({ evidence, route, shipment }) => {
  // "Cetak foto" assembles the AGL Dokumen Lampiran from the whole route, not
  // just this section — the spine is simply where ops reach for it (AC-0.3.1.2).
  const [printOpen, setPrintOpen] = React.useState(false);
  if (!evidence || (!evidence.start && !evidence.end)) return null;
  const routeStops = route?.stops ?? [];
  const stopOf = (stopId: string | null) =>
    routeStops.find((s) => s.id === stopId) ?? null;
  // The pair only shows the trip's two ends; a stop failed, cancelled or
  // skipped in between would otherwise be invisible here (1D, TN2.1).
  const endIds = [evidence.start?.stopId, evidence.end?.stopId];
  const closedBetween = routeStops
    .filter((s) => hasStopOutcome(s) && !endIds.includes(s.id))
    .sort((a, b) => a.sequence - b.sequence);
  return (
    <section>
      <div className="flex items-start justify-between gap-2">
        <SectionTitle>Start &amp; end evidence</SectionTitle>
        <Button
          tone="neutral"
          style="stroke"
          size="xs"
          onClick={() => setPrintOpen(true)}
        >
          <i className="ri-printer-line mr-1.5 text-[13px]" />
          Cetak foto
        </Button>
      </div>
      <p className="-mt-1 mb-3 text-[11px] text-text-sub-600">
        4W trip <span className="font-mono">{evidence.routeCode}</span> · photos
        taken by the driver at each end.
      </p>
      {printOpen && (
        <React.Suspense fallback={null}>
          <EvidencePrintModal
            open
            onClose={() => setPrintOpen(false)}
            shipment={shipment}
          />
        </React.Suspense>
      )}
      <div className="space-y-2.5">
        {evidence.start && (
          <Direct4WSideCard
            label="Start"
            side={evidence.start}
            stop={stopOf(evidence.start.stopId)}
          />
        )}
        {closedBetween.map((stop) => (
          <EvidenceStopCard key={stop.id} stop={stop} />
        ))}
        {evidence.end && (
          <Direct4WSideCard
            label="End"
            side={evidence.end}
            stop={stopOf(evidence.end.stopId)}
          />
        )}
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Timeline — status transitions and door milestones in one stream

const TIMELINE_EVENT_ICON: Record<string, string> = {
  STATUS_CHANGE: 'ri-swap-line',
  WORKFLOW_MILESTONE: 'ri-camera-line',
  NOTE: 'ri-sticky-note-line',
  STOP_FAILED: 'ri-close-circle-line',
  STOP_CANCELLED: 'ri-forbid-2-line',
};

const stopFailedMetadata = (
  event: ShipmentTimelineEvent
): Partial<StopFailedTimelineMetadata> =>
  event.eventType === 'STOP_FAILED' && event.metadata
    ? (event.metadata as Partial<StopFailedTimelineMetadata>)
    : {};

const stopCancelledMetadata = (
  event: ShipmentTimelineEvent
): Partial<StopCancelledTimelineMetadata> =>
  event.eventType === 'STOP_CANCELLED' && event.metadata
    ? (event.metadata as Partial<StopCancelledTimelineMetadata>)
    : {};

/** A driver fail with a "come back later" reason (EC5009 / EB3007). */
const isRescheduleEvent = (event: ShipmentTimelineEvent): boolean =>
  event.eventType === 'STOP_FAILED' &&
  isRescheduleReason(stopFailedMetadata(event).reasonCode);

/**
 * A door milestone that captured a signature gets the pen (design 4A); a
 * reschedule fail gets the clock, like its block on the stop.
 */
const timelineEventIcon = (event: ShipmentTimelineEvent): string => {
  if (
    event.eventType === 'WORKFLOW_MILESTONE' &&
    event.attachments.some((a) => a.type === 'SIGNATURE')
  ) {
    return 'ri-quill-pen-line';
  }
  if (isRescheduleEvent(event)) return 'ri-time-line';
  return TIMELINE_EVENT_ICON[event.eventType] ?? 'ri-circle-line';
};

/** Icon colour per event; everything not listed stays neutral. */
const timelineIconTone = (event: ShipmentTimelineEvent): string => {
  if (isRescheduleEvent(event)) return 'text-(--state-warning-dark)';
  if (event.eventType === 'STOP_FAILED') return 'text-error-base';
  return 'text-text-sub-600';
};

const withReason = (head: string, label: string | null | undefined) =>
  label ? `${head} · ${label}` : head;

/**
 * Title for a non-status event. A STOP_FAILED / STOP_CANCELLED row is titled
 * in English from its metadata ("Stop 2 failed by driver · <label>", "Stop 3
 * cancelled by Ops · <label>"; the label stays Indonesian); the server's own
 * title is the fallback when the metadata lacks the stop.
 */
function timelineTitle(event: ShipmentTimelineEvent): string {
  if (event.eventType === 'STOP_FAILED') {
    const meta = stopFailedMetadata(event);
    if (meta.stopSequence != null) {
      return withReason(
        `Stop ${meta.stopSequence} failed by driver`,
        meta.reasonLabel
      );
    }
  }
  if (event.eventType === 'STOP_CANCELLED') {
    const meta = stopCancelledMetadata(event);
    if (meta.stopSequence != null) {
      return withReason(
        `Stop ${meta.stopSequence} cancelled by Ops`,
        meta.reasonLabel
      );
    }
  }
  if (event.title) return event.title;
  return event.milestoneKey ?? 'Event';
}

/** The note of a stop event falls back to the one in its metadata. */
const timelineNote = (event: ShipmentTimelineEvent): string | null =>
  event.note ??
  stopFailedMetadata(event).note ??
  stopCancelledMetadata(event).note ??
  null;

/**
 * What a STOP_FAILED / STOP_CANCELLED row adds under its title: the
 * reschedule marker, a fail that overrode an Ops cancel, the drops a failed
 * pickup skipped, "Not billed", and the pickup a cascaded cancel followed.
 */
const StopEventExtras: React.FC<{ event: ShipmentTimelineEvent }> = ({
  event,
}) => {
  const failed = stopFailedMetadata(event);
  const cancelled = stopCancelledMetadata(event);
  const skipped = failed.cascadedStopSequences ?? [];
  const badges: React.ReactNode[] = [];
  if (isRescheduleEvent(event)) {
    badges.push(
      <Badge key="reschedule" status="warning" size="sm">
        {RESCHEDULE_LABEL}
      </Badge>
    );
  }
  if (failed.overridesCancellation) {
    badges.push(
      <Badge key="override" status="neutral" size="sm">
        Overrides Ops cancel
      </Badge>
    );
  }
  if (event.eventType === 'STOP_CANCELLED' && isNotBilled(cancelled.category)) {
    badges.push(
      <Badge key="not-billed" status="information" size="sm">
        {CANCEL_STOP_COPY.notBilled}
      </Badge>
    );
  }
  const lines: string[] = [];
  if (skipped.length > 0) {
    lines.push(
      `Skipped · pickup failed: stop ${[...skipped]
        .sort((a, b) => a - b)
        .join(', ')}`
    );
  }
  if (cancelled.cascadedFromStopSequence != null) {
    lines.push(
      `Cancelled with stop ${cancelled.cascadedFromStopSequence} (its pickup)`
    );
  }
  if (badges.length === 0 && lines.length === 0) return null;
  return (
    <div className="mt-1 space-y-0.5">
      {badges.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">{badges}</div>
      )}
      {lines.map((line) => (
        <p key={line} className="text-[11px] text-text-sub-600">
          {line}
        </p>
      ))}
    </div>
  );
};

/**
 * `rider:abc` → `Rider abc`, `ops:7` → `Ops 7`, `system` → `System`; emails
 * pass through.
 */
function formatActor(actor: string | null): string | null {
  if (!actor) return null;
  if (actor === 'system') return 'System';
  if (actor.startsWith('rider:')) return `Rider ${actor.slice(6)}`;
  if (actor.startsWith('ops:')) return `Ops ${actor.slice(4)}`;
  return actor;
}

/**
 * One captured field. A file field (IMAGE, SIGNATURE) renders as tiles with the
 * same lightbox as the driver-evidence section — a signature as the 2:1 white
 * variant, badged when ops filled it; everything else is a labelled value.
 * `value` is always an array from BE, so TEXT/NUMBER just join.
 */
const TimelineAttachmentRow: React.FC<{
  attachment: ShipmentTimelineAttachment;
  submittedVia?: MilestoneSubmittedVia | null;
  /** Context for the photo `alt` (e.g. the event title); defaults to the name. */
  context?: string;
}> = ({ attachment, submittedVia, context }) => {
  if (isFileFieldType(attachment.type)) {
    const isSignature = attachment.type === 'SIGNATURE';
    return (
      <div>
        <p className="text-[11px] text-text-sub-600">
          {isSignature ? signatureLabel(attachment.name) : attachment.name}
        </p>
        <PhotoTiles
          urls={attachment.value.map(String)}
          variant={isSignature ? 'signature' : 'photo'}
          label={attachment.name}
          altPrefix={
            context ? `${attachment.name} · ${context}` : attachment.name
          }
          badge={
            isSignature && submittedVia === 'OPS' ? (
              <OpsSignatureBadge />
            ) : undefined
          }
        />
      </div>
    );
  }
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[11px] text-text-sub-600">{attachment.name}</span>
      <span className="text-[12px] text-text-strong-950 text-right">
        {attachment.value.length > 0 ? attachment.value.join(', ') : '—'}
      </span>
    </div>
  );
};

const TimelineEventCard: React.FC<{
  event: ShipmentTimelineEvent;
  showConnector: boolean;
}> = ({ event, showConnector }) => {
  const actor = formatActor(event.createdBy);
  const isStatusChange = event.eventType === 'STATUS_CHANGE';
  const isStopEvent =
    event.eventType === 'STOP_FAILED' || event.eventType === 'STOP_CANCELLED';
  const title = timelineTitle(event);
  const note = timelineNote(event);
  return (
    <li className="flex gap-3" data-testid={`timeline-event-${event.id}`}>
      {/* Rail: dot and connector share one flex column, so the line is centred
          under the dot by layout rather than by an offset that has to be kept
          in sync with the dot's size (the previous absolute dot sat 8px off the
          border, which read as no line at all). Per-event connector → the rail
          stops at the last dot instead of running past it. */}
      <div className="flex flex-col items-center self-stretch">
        <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-stroke-soft-200 bg-bg-white-0">
          <i
            data-testid="timeline-event-icon"
            aria-hidden
            className={`${timelineEventIcon(event)} text-[10px] ${timelineIconTone(
              event
            )}`}
          />
        </span>
        {showConnector && (
          <span className="my-1 w-0.5 flex-1 bg-stroke-soft-200" />
        )}
      </div>

      <div className={`min-w-0 flex-1 ${showConnector ? 'pb-4' : ''}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {isStatusChange ? (
              <span className="flex flex-wrap items-center gap-1.5">
                <ShipmentStatusBadge status={event.status} />
                {event.fromStatus && (
                  <span className="text-[11px] text-text-sub-600">
                    from{' '}
                    {SHIPMENT_STATUS_LABELS[event.fromStatus] ??
                      event.fromStatus}
                  </span>
                )}
              </span>
            ) : (
              <span className="text-[12px] font-semibold text-text-strong-950">
                {title}
              </span>
            )}
          </div>
          <span className="shrink-0 text-[11px] tabular-nums text-text-sub-600">
            {formatDateTime(event.occurredAt)}
          </span>
        </div>

        {/* A milestone does not move the status, so name where the shipment
            stood — otherwise the reader has to scan up for the last
            transition. */}
        {!isStatusChange && (
          <p className="mt-0.5 text-[11px] text-text-sub-600">
            {SHIPMENT_STATUS_LABELS[event.status] ?? event.status}
            {actor ? ` · ${actor}` : ''}
          </p>
        )}
        {isStatusChange && actor && (
          <p className="mt-0.5 text-[11px] text-text-sub-600">{actor}</p>
        )}
        {note && (
          <p className="mt-1 text-[11px] leading-snug text-text-sub-600">
            {note}
          </p>
        )}
        {isStopEvent && <StopEventExtras event={event} />}

        {event.attachments.length > 0 && (
          <div className="mt-1.5 space-y-1.5 rounded-gsm border border-stroke-soft-200 bg-bg-weak-50 p-2.5">
            {event.attachments.map((a) => (
              <TimelineAttachmentRow
                key={a.key}
                attachment={a}
                submittedVia={event.submittedVia}
                context={isStopEvent ? title : undefined}
              />
            ))}
          </div>
        )}
      </div>
    </li>
  );
};

const TimelineSection: React.FC<{ events?: ShipmentTimelineEvent[] }> = ({
  events,
}) => {
  // Absent on older BE builds, empty for shipments created before the
  // timeline existed — both mean "nothing to show", not "broken".
  if (!events || events.length === 0) return null;
  return (
    <section>
      <SectionTitle>Timeline</SectionTitle>
      <p className="-mt-1 mb-3 text-[11px] text-text-sub-600">
        Newest first · status changes and driver milestones.
      </p>
      <ol>
        {events.map((event, i) => (
          <TimelineEventCard
            key={event.id}
            event={event}
            showConnector={i < events.length - 1}
          />
        ))}
      </ol>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Items — table view of every PO under this AWB

const ItemsSection: React.FC<{ items: Item[]; onChanged: () => void }> = ({
  items,
  onChanged,
}) => {
  const [editItem, setEditItem] = useState<Item | null>(null);
  const [cancelItem, setCancelItem] = useState<Item | null>(null);
  const [cancelling, setCancelling] = useState(false);

  // Item → the shared EditPackageModal's shape (also used by the Packages page).
  const toEditable = (it: Item): EditablePackage => ({
    id: it.id,
    itemName: it.itemName,
    po: it.code,
    weight: it.weight,
    volume: it.volume,
    koli: it.koli,
  });

  const handleCancel = async () => {
    if (!cancelItem) return;
    setCancelling(true);
    try {
      await cancelPackage(cancelItem.id);
      toast.success('Item dibatalkan', {
        description: `${cancelItem.itemName || cancelItem.code} telah dibatalkan.`,
      });
      setCancelItem(null);
      onChanged();
    } catch (err) {
      toast.error('Gagal membatalkan', {
        description: getBackendErrorMessage(err),
      });
    } finally {
      setCancelling(false);
    }
  };

  if (items.length === 0) {
    return (
      <section>
        <SectionTitle>Items</SectionTitle>
        <div className="rounded-lg border border-dashed border-stroke-soft-200 px-4 py-8 text-center">
          <i className="ri-inbox-line text-2xl text-text-soft-400" />
          <p className="mt-2 text-xs text-text-sub-600">
            No items returned for this shipment.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section>
      <SectionTitle>Items ({items.length})</SectionTitle>
      <ul className="divide-y divide-stroke-soft-200 overflow-hidden rounded-lg border border-stroke-soft-200">
        {items.map((it) => {
          const hasDescription =
            it.description &&
            it.description.trim() &&
            it.description.trim() !== '-';
          const canEdit = EDITABLE_ITEM_STATUSES.includes(it.status);
          const canCancel = CANCELLABLE_ITEM_STATUSES.includes(it.status);
          return (
            <li key={it.id} className="bg-white px-3 py-2.5">
              {/* Top line: item code + status badge. Code is the at-a-glance
                  anchor; PO id below tends to be longer/uglier so it gets the
                  break-all treatment. */}
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-[12px] font-semibold tabular-nums text-text-strong-950">
                  {it.code}
                </span>
                <span className="flex flex-wrap items-center justify-end gap-1">
                  <ItemStatusBadge status={it.status} />
                  <ItemConditionBadge condition={it.condition} />
                </span>
              </div>
              <p className="mt-1 break-all font-mono text-[11px] tabular-nums text-text-sub-600">
                {it.itemName}
              </p>
              {it.invoice && (
                <p className="mt-1 text-[11px] text-text-sub-600">
                  <span className="text-text-sub-600">Invoice </span>
                  <span className="break-all font-mono tabular-nums text-text-sub-600">
                    {it.invoice}
                  </span>
                </p>
              )}
              <p className="mt-1 text-[11px] text-text-sub-600">
                <span className="text-text-sub-600">Qty </span>
                <span className="font-mono tabular-nums text-text-sub-600">
                  {it.quantity}
                </span>
                {/* Koli = physical boxes for this PO; distinct from `quantity`,
                    which stays 1 for CSV-imported items. */}
                {it.koli != null && (
                  <>
                    <span className="mx-1.5 text-text-soft-400">·</span>
                    <span className="text-text-sub-600">Koli </span>
                    <span className="font-mono tabular-nums text-text-sub-600">
                      {it.koli}
                    </span>
                  </>
                )}
                <span className="mx-1.5 text-text-soft-400">·</span>
                <span className="font-mono tabular-nums text-text-sub-600">
                  {formatWeight(it.weight)}
                </span>
                <span className="mx-1.5 text-text-soft-400">·</span>
                <span className="font-mono tabular-nums text-text-sub-600">
                  {formatVolume(it.volume)}
                </span>
                {hasDescription && (
                  <>
                    <span className="mx-1.5 text-text-soft-400">·</span>
                    <span className="text-text-sub-600">{it.description}</span>
                  </>
                )}
              </p>
              <ReturNote item={it} />
              <ItemLegs legs={it.legs} />
              {(canEdit || canCancel) && (
                <div className="mt-2 flex items-center gap-2">
                  {canEdit && (
                    <Button
                      tone="neutral"
                      style="stroke"
                      size="xs"
                      onClick={() => setEditItem(it)}
                    >
                      <i className="ri-edit-line mr-1" />
                      Edit
                    </Button>
                  )}
                  {canCancel && (
                    <Button
                      tone="neutral"
                      style="stroke"
                      size="xs"
                      onClick={() => setCancelItem(it)}
                    >
                      <i className="ri-close-circle-line mr-1" />
                      Cancel
                    </Button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {/* Reuses the Packages page's edit modal (both edit item weight/volume). */}
      <EditPackageModal
        pkg={editItem ? toEditable(editItem) : null}
        open={editItem !== null}
        onClose={() => setEditItem(null)}
        onSaved={onChanged}
      />

      <FormModal
        open={cancelItem !== null}
        onClose={() => (cancelling ? undefined : setCancelItem(null))}
        icon={<i className="ri-close-circle-line" />}
        iconTone="danger"
        title="Cancel this item?"
        subtitle={cancelItem?.itemName || cancelItem?.code}
        size="sm"
        closeOnBackdropClick={!cancelling}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button
              tone="neutral"
              style="stroke"
              onClick={() => setCancelItem(null)}
              disabled={cancelling}
            >
              Keep item
            </Button>
            <Button
              tone="destructive"
              onClick={handleCancel}
              disabled={cancelling}
            >
              {cancelling && (
                <i className="ri-loader-4-line mr-1.5 animate-spin" />
              )}
              Cancel item
            </Button>
          </div>
        }
      >
        <p className="text-sm text-text-sub-600">
          Item ini akan ditandai <b>cancelled</b> dan dikeluarkan dari total
          shipment. Tindakan ini tidak bisa dibatalkan.
        </p>
      </FormModal>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// OPS actions have moved OFF the detail panel to the shipments-list row kebab
// (see `pages/shipments/index.tsx`): Cancel (CREATED), Mark completed
// (IN_DELIVERY), Mark failed (in-flight). The panel is now read-only.

// ─────────────────────────────────────────────────────────────────────────────
// Cancellation — only renders when status is CANCELLED

const CancellationSection: React.FC<{ shipment: Shipment }> = ({
  shipment,
}) => (
  <section>
    <SectionTitle>Cancellation</SectionTitle>
    <dl className="divide-y divide-stroke-soft-200 border-y border-stroke-soft-200">
      <Row
        label="Cancelled at"
        value={formatDateTime(shipment.cancelledAt)}
        mono
      />
      {shipment.cancelledBy && (
        <Row label="Cancelled by" value={shipment.cancelledBy} mono />
      )}
      {shipment.cancellationReason && (
        <div className="py-2.5">
          <dt className="text-[12px] text-text-sub-600 mb-1.5">Reason</dt>
          <dd className="whitespace-pre-wrap text-[13px] text-text-sub-600 leading-relaxed bg-bg-weak-50 border border-stroke-soft-200 rounded-md px-3 py-2">
            {shipment.cancellationReason}
          </dd>
        </div>
      )}
    </dl>
  </section>
);

// ─────────────────────────────────────────────────────────────────────────────
// Skeleton

const DetailSkeleton: React.FC = () => (
  <div className="space-y-7 animate-pulse">
    <div className="space-y-2">
      <div className="h-4 w-24 rounded-md bg-bg-soft-200" />
      <div className="h-3 w-40 rounded-md bg-bg-weak-50" />
    </div>
    <div>
      <div className="h-3 w-16 rounded-md bg-bg-soft-200 mb-3" />
      <div className="space-y-2 border-y border-stroke-soft-200 py-2">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex justify-between gap-4">
            <div className="h-3 w-20 rounded-md bg-bg-weak-50" />
            <div className="h-3 w-36 rounded-md bg-bg-weak-50" />
          </div>
        ))}
      </div>
    </div>
    <div>
      <div className="h-3 w-16 rounded-md bg-bg-soft-200 mb-3" />
      <div className="grid grid-cols-2 gap-3">
        <div className="h-16 rounded-lg bg-bg-weak-50" />
        <div className="h-16 rounded-lg bg-bg-weak-50" />
      </div>
    </div>
    <div>
      <div className="h-3 w-16 rounded-md bg-bg-soft-200 mb-3" />
      <div className="space-y-3">
        <div className="h-20 rounded-lg bg-bg-weak-50" />
        <div className="h-20 rounded-lg bg-bg-weak-50" />
      </div>
    </div>
  </div>
);

export default ShipmentDetailPanel;
